import { Box, Card, CardBody, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ApexOptions } from "apexcharts";
import { ReactNode, useState } from "react";
import Chart from "react-apexcharts";
import { adminGetAnalysisTrend } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import { colors } from "../../../styles";
import { AdminAnalysisTrendUnit, IAdminAnalysisTrend, IAdminAnalysisTrendCount, IAdminAnalysisTrendPoint } from "../../../types";

type TrendMetric = "signup" | "search" | "chat";
type TrendSeriesKey = keyof IAdminAnalysisTrendCount;

const SERIES: { key: TrendSeriesKey; name: string; color: string }[] = [
    { key: "total", name: "전체", color: colors.g1 },
    { key: "owner", name: "개국약사", color: colors.blue },
    { key: "pharmacist", name: "약사", color: colors.covalt },
    { key: "student", name: "약대생", color: colors.teal },
    { key: "member", name: "멤버", color: colors.link },
];

const METRICS: { key: TrendMetric; title: string; description: Record<AdminAnalysisTrendUnit, string> }[] = [
    {
        key: "signup",
        title: "누적 가입자 수",
        description: {
            day: "해당 시점까지 쌓인 가입자 수입니다.",
            month: "해당 시점까지 쌓인 가입자 수입니다.",
        },
    },
    {
        key: "search",
        title: "검색 활성 유저",
        description: {
            day: "한 번 이상 검색한 사용자 수입니다.",
            month: "당월 이틀 이상 검색한 사용자 수입니다.",
        },
    },
    {
        key: "chat",
        title: "채팅 활성 유저",
        description: {
            day: "피드를 한 번 이상 조회한 사용자 수입니다.",
            month: "당월 이틀 이상 피드를 조회한 사용자 수입니다.",
        },
    },
];

function safeNumber(value: number | undefined) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatCount(value: number) {
    return Math.round(safeNumber(value)).toLocaleString("ko-KR");
}

function formatShare(part: number, total: number) {
    if (total <= 0) return "0%";
    const ratio = Math.round((safeNumber(part) / total) * 1000) / 10;
    const text = Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(1);
    return `${text}%`;
}

function formatAxisLabel(label: string, unit: AdminAnalysisTrendUnit) {
    const parts = label.split("-");
    if (unit === "month" && parts.length >= 2) {
        return `${parts[0].slice(2)}.${parts[1]}`;
    }
    if (unit === "day" && parts.length >= 3) {
        return `${Number(parts[1])}/${Number(parts[2])}`;
    }
    return label;
}

function isAnalysisTrend(data: IAdminAnalysisTrend | undefined): data is IAdminAnalysisTrend {
    return Boolean(data && Array.isArray(data.points));
}

function AnalysisCard({ children }: { children: ReactNode }) {
    return (
        <Card
            width={"100%"}
            border={`0.5px solid ${colors.g4}`}
            borderRadius={10}
            shadow={"md"}
            backgroundColor={colors.white}
        >
            <CardBody>{children}</CardBody>
        </Card>
    );
}

function TrendChart({
    title,
    description,
    metric,
    unit,
    points,
}: {
    title: string;
    description: string;
    metric: TrendMetric;
    unit: AdminAnalysisTrendUnit;
    points: IAdminAnalysisTrendPoint[];
}) {
    const categories = points.map((point) => formatAxisLabel(point.label, unit));
    const series = SERIES.map((item) => ({
        name: item.name,
        data: points.map((point) => safeNumber(point[metric]?.[item.key])),
    }));

    const options: ApexOptions = {
        chart: {
            id: `analysis-trend-${metric}-${unit}`,
            type: "line",
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "inherit",
            animations: { speed: 280 },
        },
        colors: SERIES.map((item) => item.color),
        stroke: {
            curve: "straight",
            width: [3, 2, 2, 2, 2],
        },
        dataLabels: { enabled: false },
        markers: {
            size: unit === "month" ? 4 : 0,
            hover: { size: 5 },
        },
        legend: {
            position: "bottom",
            horizontalAlign: "left",
            fontSize: "13px",
            fontWeight: 500,
            labels: { colors: colors.g1 },
            markers: { size: 8, offsetX: -2 },
            itemMargin: { horizontal: 10, vertical: 4 },
        },
        grid: {
            borderColor: colors.g4,
            strokeDashArray: 4,
        },
        xaxis: {
            categories,
            tooltip: { enabled: false },
            labels: {
                rotate: unit === "day" ? -45 : 0,
                rotateAlways: unit === "day",
                hideOverlappingLabels: true,
                style: { colors: colors.g2, fontSize: "12px" },
            },
            axisBorder: { color: colors.g4 },
            axisTicks: { color: colors.g4 },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: {
                formatter: (value) => formatCount(Number(value)),
                style: { colors: colors.g2, fontSize: "12px" },
            },
        },
        tooltip: {
            shared: true,
            intersect: false,
            x: {
                formatter: (_value, opts) => points[opts?.dataPointIndex ?? -1]?.label ?? "",
            },
            y: {
                formatter: (value, opts) => {
                    const count = formatCount(Number(value));
                    if ((opts?.seriesIndex ?? 0) === 0) return count;

                    const point = points[opts?.dataPointIndex ?? -1];
                    const total = safeNumber(point?.[metric]?.total);
                    return `${count} (${formatShare(Number(value), total)})`;
                },
            },
        },
    };

    return (
        <AnalysisCard>
            <Stack spacing={1} marginBottom={2}>
                <Text fontSize={18} fontWeight={700} color={colors.black}>{title}</Text>
                <Text fontSize={13} fontWeight={400} color={colors.g2}>{description}</Text>
            </Stack>
            {points.length > 0 ? (
                <Box width={"100%"} minW={0}>
                    <Chart
                        key={`${metric}-${unit}-${points.length}-${points[0]?.label ?? ""}`}
                        type={"line"}
                        height={300}
                        width={"100%"}
                        series={series}
                        options={options}
                    />
                </Box>
            ) : (
                <Text fontSize={14} fontWeight={400} color={colors.g2} paddingY={8}>표시할 추이가 없습니다.</Text>
            )}
        </AnalysisCard>
    );
}

export default function Trend() {
    const [unit, setUnit] = useState<AdminAnalysisTrendUnit>("day");

    const { data, isLoading, isError } = useQuery<IAdminAnalysisTrend>({
        queryKey: ["adminGetAnalysisTrend", unit],
        queryFn: () => adminGetAnalysisTrend({ unit }),
    });

    const analysis = isAnalysisTrend(data) ? data : undefined;

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10} spacing={8}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={4}>
                    <Text fontSize={28} fontWeight={700} color={colors.black}>추이 분석</Text>
                    <HStack spacing={0} border={`0.5px solid ${colors.g4}`} borderRadius={8} overflow={"hidden"}>
                        {(["day", "month"] as const).map((item) => {
                            const selected = unit === item;
                            return (
                                <Box
                                    as={"button"}
                                    key={item}
                                    onClick={() => setUnit(item)}
                                    px={4}
                                    py={2}
                                    fontSize={14}
                                    fontWeight={selected ? 700 : 500}
                                    backgroundColor={selected ? colors.blue : colors.white}
                                    color={selected ? colors.white : colors.g1}
                                >
                                    {item === "day" ? "일 기준" : "월 기준"}
                                </Box>
                            );
                        })}
                    </HStack>
                </HStack>

                {isLoading ? (
                    <Spinner color={colors.blue} />
                ) : isError || !analysis ? (
                    <Text fontSize={14} fontWeight={400} color={colors.g2}>추이 분석 데이터를 불러오지 못했습니다.</Text>
                ) : (
                    <Stack spacing={5}>
                        {METRICS.map((metric) => (
                            <TrendChart
                                key={metric.key}
                                title={metric.title}
                                description={metric.description[unit]}
                                metric={metric.key}
                                unit={unit}
                                points={analysis.points}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </AdminOnly>
    );
}
